export const CHAT_STREAM_PENDING_HTTP_STATUS = 503;
export const CHAT_STREAM_PENDING_ERROR = 'stream_pending';
export const CHAT_STREAM_RESUME_RETRY_DELAYS_MS = [
  500, 1000, 2000, 4000, 8000, 16_000, 16_000,
] as const;
export const CHAT_STREAM_PENDING_POLL_INTERVAL_MS = 2_000;
export const CHAT_STREAM_PENDING_MAX_WAIT_MS = 150_000;

const sleep = (delayMs: number): Promise<void> =>
  new Promise((resolve) => {
    setTimeout(resolve, delayMs);
  });

export const isChatStreamPendingResponse = (response: Response): boolean =>
  response.status === CHAT_STREAM_PENDING_HTTP_STATUS;

export const shouldResumeChatStreamAfterFinish = ({
  isAbort,
  isDisconnect,
  isError = false,
  isTerminalError = false,
  intentionalAbort,
}: {
  isAbort: boolean;
  isDisconnect: boolean;
  isError?: boolean;
  isTerminalError?: boolean;
  intentionalAbort: boolean;
}): boolean => {
  if (intentionalAbort) {
    return false;
  }

  return isDisconnect || isAbort || (isError && !isTerminalError);
};

export const CHAT_TURN_INTERRUPTED_HEADER = 'X-Chat-Turn-Interrupted';

export const isChatTurnInterruptedResponse = (response: Response): boolean =>
  response.status === 204 &&
  response.headers.get(CHAT_TURN_INTERRUPTED_HEADER) === '1';

export const shouldQueueChatSend = ({
  status,
  isResumingStream,
  skipQueue,
}: {
  status: 'streaming' | 'submitted' | 'ready' | 'error';
  isResumingStream: boolean;
  skipQueue: boolean;
}): boolean => {
  if (skipQueue) {
    return false;
  }

  return isResumingStream || status === 'streaming' || status === 'submitted';
};

export const shouldRefetchChatHistoryAfterResumeIdle = ({
  resumeStatus,
  hasPendingConfirmation,
}: {
  resumeStatus: number;
  hasPendingConfirmation: boolean;
}): boolean => resumeStatus === 204 && !hasPendingConfirmation;

export const shouldClearChatStreamResumeFlag = ({
  resumeStatus,
}: {
  resumeStatus: number;
}): boolean =>
  resumeStatus !== 200 && resumeStatus !== CHAT_STREAM_PENDING_HTTP_STATUS;

export class ChatStreamPendingError extends Error {
  readonly statusCode = CHAT_STREAM_PENDING_HTTP_STATUS;

  constructor() {
    super(CHAT_STREAM_PENDING_ERROR);
    this.name = 'ChatStreamPendingError';
  }
}

export const isChatStreamPendingResumeError = (error: unknown): boolean => {
  if (error instanceof ChatStreamPendingError) {
    return true;
  }

  if (typeof error === 'string') {
    return error.toLowerCase().includes(CHAT_STREAM_PENDING_ERROR);
  }

  if (!(error instanceof Error)) {
    return false;
  }

  return (
    error.name === 'ChatStreamPendingError' ||
    error.message.toLowerCase().includes(CHAT_STREAM_PENDING_ERROR)
  );
};

export const shouldShowChatReconnecting = ({
  isResumingStream,
  status,
}: {
  isResumingStream: boolean;
  status: 'streaming' | 'submitted' | 'ready' | 'error';
}): boolean =>
  isResumingStream && status !== 'streaming' && status !== 'submitted';

export const startChatStreamResume = ({
  resumeStream,
  onResumeFailed,
}: {
  resumeStream?: () => void | Promise<unknown>;
  onResumeFailed?: () => void;
}): void => {
  const handleResumeFailure = (error: unknown) => {
    console.warn('Failed to resume chat stream', error);

    if (!isChatStreamPendingResumeError(error)) {
      onResumeFailed?.();
    }
  };

  try {
    void Promise.resolve(resumeStream?.()).catch(handleResumeFailure);
  } catch (error: unknown) {
    handleResumeFailure(error);
  }
};

export const shouldDisplayChatStreamError = ({
  hasError,
  isResumingStream,
  status,
  isRecoverableError = false,
}: {
  hasError: boolean;
  isResumingStream: boolean;
  status: 'streaming' | 'submitted' | 'ready' | 'error';
  isRecoverableError?: boolean;
}): boolean => {
  if (!hasError || isResumingStream || isRecoverableError) {
    return false;
  }

  return status !== 'streaming' && status !== 'submitted';
};

type ResumeHistoryMessage = {
  id: string;
  role?: string;
  parts?: readonly unknown[];
};

const isTraceMetadataPart = (
  part: unknown,
): part is { data: { type: string } } => {
  if (typeof part !== 'object' || part === null) {
    return false;
  }

  const data = (part as { data?: unknown }).data;

  if (typeof data !== 'object' || data === null) {
    return false;
  }

  return (data as { type?: unknown }).type === 'data-traceMetadata';
};

const traceMetadataParts = <T extends ResumeHistoryMessage>(message: T) =>
  (message.parts ?? []).filter(isTraceMetadataPart);

const withTraceMetadataFromLocal = <T extends ResumeHistoryMessage>(
  saved: T,
  local: T,
): T => {
  const incoming = traceMetadataParts(local);

  if (incoming.length === 0 || traceMetadataParts(saved).length > 0) {
    return saved;
  }

  return {
    ...saved,
    parts: [...(saved.parts ?? []), ...incoming],
  };
};

export const mergeChatHistoryAfterResumeIdle = <
  T extends ResumeHistoryMessage,
>({
  pageHistory,
  localMessages,
}: {
  pageHistory: T[];
  localMessages: T[];
}): T[] => {
  const pageById = new Map(pageHistory.map((message) => [message.id, message]));
  const pageIds = new Set(pageById.keys());
  const localIds = new Set(localMessages.map((message) => message.id));
  const pageStartId = pageHistory[0]?.id;
  const pageStartIndex =
    pageStartId === undefined
      ? -1
      : localMessages.findIndex((message) => message.id === pageStartId);
  const overlapIndex = localMessages.findIndex((message) =>
    pageIds.has(message.id),
  );
  const windowStart = pageStartIndex >= 0 ? pageStartIndex : overlapIndex;
  const pageHasSavedAssistant = pageHistory.some(
    (message) => message.role === 'assistant' && !localIds.has(message.id),
  );
  const droppedAssistants: T[] = [];
  const mergedLocal = localMessages.flatMap((message, index) => {
    const saved = pageById.get(message.id);

    if (saved) {
      return [withTraceMetadataFromLocal(saved, message)];
    }

    const beforePage = windowStart < 0 || index < windowStart;

    if (!beforePage && message.role === 'assistant' && pageHasSavedAssistant) {
      droppedAssistants.push(message);

      return [];
    }

    return [message];
  });
  const newFromPage = pageHistory.filter(
    (message) => !localIds.has(message.id),
  );
  const merged = [...mergedLocal, ...newFromPage];
  const traceDonor = droppedAssistants.findLast(
    (message) => traceMetadataParts(message).length > 0,
  );

  if (!traceDonor) {
    return merged;
  }

  const replacementIndex = merged.findLastIndex(
    (message) =>
      message.role === 'assistant' &&
      pageIds.has(message.id) &&
      !localIds.has(message.id),
  );

  if (replacementIndex < 0) {
    return merged;
  }

  return merged.map((message, index) =>
    index === replacementIndex
      ? withTraceMetadataFromLocal(message, traceDonor)
      : message,
  );
};

const waitForResumeRetry = async (attempt: number): Promise<boolean> => {
  if (attempt >= CHAT_STREAM_RESUME_RETRY_DELAYS_MS.length) {
    return false;
  }

  await sleep(CHAT_STREAM_RESUME_RETRY_DELAYS_MS[attempt]);

  return true;
};

const isChatStreamPendingBody = async (
  response: Response,
): Promise<boolean> => {
  try {
    const body: unknown = await response.clone().json();

    return (
      typeof body === 'object' &&
      body !== null &&
      (body as { error?: unknown }).error === CHAT_STREAM_PENDING_ERROR
    );
  } catch {
    return false;
  }
};

export const fetchChatStreamResume = async (
  input: RequestInfo | URL,
  init?: RequestInit,
  { now = () => Date.now() }: { now?: () => number } = {},
): Promise<Response> => {
  const startedAt = now();
  let retryAttempt = 0;

  for (;;) {
    try {
      const response = await fetch(input, init);
      const upstreamDown = response.status >= 500;

      if (!upstreamDown) {
        return response;
      }

      const turnStillPending =
        isChatStreamPendingResponse(response) &&
        (await isChatStreamPendingBody(response));

      if (turnStillPending) {
        if (now() - startedAt >= CHAT_STREAM_PENDING_MAX_WAIT_MS) {
          return response;
        }

        await response.body?.cancel().catch(() => undefined);
        await sleep(CHAT_STREAM_PENDING_POLL_INTERVAL_MS);
        continue;
      }

      if (retryAttempt >= CHAT_STREAM_RESUME_RETRY_DELAYS_MS.length) {
        return response;
      }

      await response.body?.cancel().catch(() => undefined);
      await sleep(CHAT_STREAM_RESUME_RETRY_DELAYS_MS[retryAttempt]);
      retryAttempt += 1;
    } catch (error: unknown) {
      if (!(await waitForResumeRetry(retryAttempt))) {
        throw error;
      }

      retryAttempt += 1;
    }
  }
};
